// REDUX
import { useDispatch } from "react-redux";

// API
import { useLazyGetBankDetailsByAccDigitsQuery } from "src/store/features/api/generated";

// BENEFICIARY SLICE
import {
  resetNonAddressFieldsThunk,
  updateBeneficiaryAddressField,
  updateBankAddressField,
  setBeneficiaryAddress,
  setBankAddress,
  setStep,
  setCurrency,
  setBeneficiaryName,
  setBankName,
  setIbanAccNumber,
  setAbaRoutingNumberNumber,
  setIntitutionNumber,
  setSortCodeOrBsb,
  setSwiftCode,
  setTransitNumber,
  setIsBankAddressEntered,
  setIsBeneficiaryAddressEntered,
  useBeneficiarySlice,
  setBeneficiaryAddressSearch,
  setBankAddressSearch,
  setIdentityType,
  setAccountType,
} from "src/store/features/beneficiary.slice";

// HOOKS
import { useValidateForm } from "./useValidateForm";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

// CONSTANTS
import { AVAILABLE_CURRENCIES } from "src/components/settings/payment/constants/currencies";

export const useBankForm = () => {
  /*********/
  /* HOOKS */
  /*********/
  const dispatch = useDispatch();

  const {
    validateFields,
    formErrors,
    isBankAddressEntered,
    isBeneficiaryAddressEntered,
    setFormErrors,
  } = useValidateForm();

  const { currency, bankName, beneficiaryAddress, ibanAccNumber } = useBeneficiarySlice();

  /*************
   * API CALLS *
   *************/
  const [
    getBankDetailsByAccDigits,
    { data: isBankDetailsPresent, isLoading: isBankDetailsLoading },
  ] = useLazyGetBankDetailsByAccDigitsQuery();

  /************/
  /* HANDLERS */
  /************/
  const handleCurrencySelection = (e: any) => {
    const currency = AVAILABLE_CURRENCIES.find((curr) => curr.value === e.target.value);
    if (currency) {
      dispatch(setCurrency(currency));
      setFormErrors({});
      dispatch(resetNonAddressFieldsThunk());
    }
  };

  const handleConfirmBankDetails = async () => {
    trackEvent(
      "User clicked on Confirm Bank Details",
      {
        currency,
        bankName,
        beneficiaryAddress,
      },
      "Payment",
    );

    const isValid = validateFields();

    if (isValid) {
      await getBankDetailsByAccDigits({
        ibanAccNumber,
      })
        .unwrap()
        .then((data) => {
          if (!data) {
            dispatch(setStep("Summary"));
          }
        });
    }
  };

  const handleAddressFields = (data: any, type: string) => {
    if (data) {
      if (type === "beneficiary") {
        setFormErrors({
          ...formErrors,
          beneficiaryStreetAddress: "",
          beneficiaryCity: "",
          beneficiaryCountry: "",
          beneficiaryState: "",
          beneficiaryPostcode: "",
        });
        dispatch(setBeneficiaryAddress(data));
        dispatch(setIsBeneficiaryAddressEntered(true));
      } else {
        setFormErrors({
          ...formErrors,
          bankStreet: "",
          bankCity: "",
          bankCountry: "",
          bankState: "",
          bankPostcode: "",
        });
        dispatch(setBankAddress(data));
        dispatch(setIsBankAddressEntered(true));
      }
    }
  };

  const handleAddressInput = (e: any, type: string) => {
    const { name, value } = e.target;
    if (type === "beneficiary") {
      const beneficiaryField = "beneficiary" + name.charAt(0).toUpperCase() + name.slice(1);
      removeInputError(beneficiaryField);
      dispatch(updateBeneficiaryAddressField({ name, value }));
    } else if (type === "bank") {
      const bankField = "bank" + name.charAt(0).toUpperCase() + name.slice(1);
      removeInputError(bankField);
      dispatch(updateBankAddressField({ name, value }));
    }
  };

  const handleBeneficiaryName = (e: any) => {
    const { name, value } = e.target;
    removeInputError(name);
    dispatch(setBeneficiaryName(value));
  };

  const handleBankName = (e: any) => {
    const { name, value } = e.target;
    removeInputError(name);
    dispatch(setBankName(value));
  };

  const handleIbanAccNumber = (e: any) => {
    const { name, value } = e.target;
    if (value?.length > 0 && value?.length < 6) {
      if (currency.value === "GBP" || currency.value === "EUR") {
        setFormErrors({ ...formErrors, [name]: "IBAN must be at least 6 characters" });
      } else {
        setFormErrors({ ...formErrors, [name]: "Account Number must be at least 6 digits" });
      }
    } else {
      removeInputError(name);
    }
    dispatch(setIbanAccNumber(value));
  };

  const handleSwiftCode = (e: any) => {
    const { name, value } = e.target;
    if (value?.length > 0 && (value?.length < 8 || value?.length > 11)) {
      setFormErrors({ ...formErrors, [name]: "SWIFT Code must be 8-11 characters" });
    } else {
      removeInputError(name);
    }
    dispatch(setSwiftCode(value));
  };

  const handleInstitutionNumber = (e: any) => {
    const { name, value } = e.target;
    if (value?.length > 0 && value?.length !== 3) {
      setFormErrors({ ...formErrors, [name]: "Institution Number must be 3 digits" });
    } else {
      removeInputError(name);
    }
    dispatch(setIntitutionNumber(value));
  };

  const handleAbaRoutingNumber = (e: any) => {
    const { name, value } = e.target;
    if (value?.length > 0 && value?.length !== 9) {
      setFormErrors({ ...formErrors, [name]: "ABA Routing Number must be 9 digits" });
    } else {
      removeInputError(name);
    }
    dispatch(setAbaRoutingNumberNumber(value));
  };

  const handleSortCodeOrBsb = (e: any) => {
    const { name, value } = e.target;
    if (value?.length > 0 && value?.length !== 6) {
      if (currency.value === "AUD") {
        setFormErrors({ ...formErrors, [name]: "BSB must be 6 digits" });
      } else {
        setFormErrors({ ...formErrors, [name]: "Sort Code must be 6 digits" });
      }
    } else {
      removeInputError(name);
    }
    dispatch(setSortCodeOrBsb(value));
  };

  const handleTransitNumber = (e: any) => {
    const { name, value } = e.target;
    if (value?.length > 0 && value?.length !== 5) {
      setFormErrors({ ...formErrors, [name]: "Transit Number must be 5 digits" });
    } else {
      removeInputError(name);
    }
    dispatch(setTransitNumber(value));
  };

  const handleIdentityType = (e: any) => {
    const { name, value } = e.target;
    removeInputError(name);
    dispatch(setIdentityType(value));
  };

  const handleAccountType = (e: any) => {
    const { name, value } = e.target;
    removeInputError(name);
    dispatch(setAccountType(value));
  };

  const removeInputError = (field: string) => {
    const newErrors = { ...formErrors, [field]: "" };
    setFormErrors(newErrors);
  };

  const handleAddressSearchValue = (e: any, type: string) => {
    const { name } = e.target;
    if (type === "beneficiaryAddressSearch") {
      dispatch(setBeneficiaryAddressSearch(name));
    } else {
      dispatch(setBankAddressSearch(name));
    }
    removeInputError(name);
  };

  return {
    formErrors,
    isBankAddressEntered,
    isBeneficiaryAddressEntered,
    isBankDetailsPresent,
    isBankDetailsLoading,
    validateFields,
    handleCurrencySelection,
    handleAddressInput,
    handleAddressFields,
    handleConfirmBankDetails,
    handleBeneficiaryName,
    handleBankName,
    handleIbanAccNumber,
    handleSwiftCode,
    handleInstitutionNumber,
    handleAbaRoutingNumber,
    handleSortCodeOrBsb,
    handleTransitNumber,
    removeInputError,
    handleAddressSearchValue,
    handleIdentityType,
    handleAccountType,
  };
};
